import { RelationalDiagramData, RelationalTable, RelationalColumn, RelationalForeignKey } from '../model/ErModel';
import { DesignTestsConfig, RubricItem } from '../model/DesignTestConfig';

export interface RelationalValidationResult {
    score: number; // 0 a 100
    notaBase?: number;
    penalitzacioTotal?: number;
    intentsFallitsPrevis?: number;
    isCorrect: boolean;
    feedback: Array<{
        type: 'success' | 'error' | 'warning';
        message: string;
        diffType?: 'missing' | 'surplus' | 'mismatch' | 'correct';
        itemLabel?: string;
        expected?: string;
        actual?: string;
        table?: string;
        pointsEarned?: number;
        maxPoints?: number;
    }>;
    summary: {
        tablesExpected: number;
        tablesMatched: number;
        columnsExpected: number;
        columnsMatched: number;
        typesMatched: number;
        fksExpected: number;
        fksMatched: number;
    };
}

const norm = (s?: string) => (s || '').trim().toLowerCase();

/**
 * Normalitza tipus SQL bàsics: 'varchar(50)' -> 'varchar', 'integer' -> 'int'
 */
const normalizeType = (t?: string): string => {
    let clean = norm(t).split('(')[0].trim();
    if (clean === 'integer') return 'int';
    if (clean === 'character varying') return 'varchar';
    if (clean === 'bool') return 'boolean';
    return clean;
};

/**
 * Motor de validació per al Model Relacional de Taules amb tipus i relacions FK
 * Suporta rúbrica configurable i penalització per intents fallits previs.
 */
export const validateRelationalDiagram = (
    studentData: RelationalDiagramData,
    solutionData: RelationalDiagramData,
    customConfig?: DesignTestsConfig | null,
    intentsFallitsPrevis: number = 0
): RelationalValidationResult => {
    const feedback: RelationalValidationResult['feedback'] = [];

    const solTables: RelationalTable[] = solutionData.tables || [];
    const stuTables: RelationalTable[] = studentData.tables || [];
    const solFKs: RelationalForeignKey[] = solutionData.foreignKeys || [];
    const stuFKs: RelationalForeignKey[] = studentData.foreignKeys || [];

    const rubricMap = new Map<string, RubricItem>();
    // Índex normalitzat per casar ítems de rúbrica desats amb formats d'ID antics
    // (prefixos / majúscules) amb els IDs que genera el validador actual.
    const rubricNormMap = new Map<string, RubricItem>();
    const normalizeRubricKey = (raw: string): string => {
        let k = (raw || '').toLowerCase();
        k = k.replace(/^(rel_table_|rel_col_|rel_fk_|table_|column_|pk_|fk_|t_|c_)/, '');
        return k.trim();
    };
    const hasCustomRubric = Boolean(customConfig && Array.isArray(customConfig.items) && customConfig.items.length > 0);
    if (hasCustomRubric && customConfig?.items) {
        customConfig.items.forEach(it => {
            rubricMap.set(it.id, it);
            rubricNormMap.set(normalizeRubricKey(it.id), it);
            if ((it as any).targetKey) rubricNormMap.set(normalizeRubricKey(String((it as any).targetKey)), it);
        });
    }

    let earnedScore = 0;
    let totalMaxConfigured = 0;

    const checkRubric = (candidates: string | string[], defaultMax = 0): { enabled: boolean; points: number } => {
        if (!hasCustomRubric) {
            return { enabled: true, points: defaultMax };
        }
        const list = Array.isArray(candidates) ? candidates : [candidates];
        let item: RubricItem | undefined;
        for (const c of list) {
            item = rubricMap.get(c) || rubricNormMap.get(normalizeRubricKey(c));
            if (item) break;
        }
        if (!item || !item.enabled) {
            return { enabled: false, points: 0 };
        }
        return { enabled: true, points: Number(item.points) || 0 };
    };

    let matchedTables = 0;
    let expectedCols = 0;
    let matchedCols = 0;
    let matchedTypes = 0;
    let matchedFKs = 0;

    // 1. Validar Taules i Columnes (amb tipus i PKs)
    solTables.forEach(solT => {
        const tableRubricId = `rel_table_${solT.id || solT.name}`;
        const tableRubric = checkRubric([tableRubricId, `rel_table_${solT.name}`, solT.id, solT.name], 25);
        if (tableRubric.enabled) totalMaxConfigured += tableRubric.points;

        const foundStuT = stuTables.find(t => norm(t.name) === norm(solT.name));
        if (!foundStuT) {
            if (tableRubric.enabled) {
                feedback.push({
                    type: 'error',
                    diffType: 'missing',
                    itemLabel: solT.name,
                    expected: `Taula "${solT.name}"`,
                    actual: '(No entregada)',
                    message: `Falta la taula "${solT.name}".`,
                    table: solT.name,
                    maxPoints: tableRubric.points,
                    pointsEarned: 0
                });
            }
            return;
        }

        matchedTables++;
        if (tableRubric.enabled) {
            earnedScore += tableRubric.points;
            feedback.push({
                type: 'success',
                diffType: 'correct',
                itemLabel: solT.name,
                expected: `Taula "${solT.name}"`,
                actual: `Taula "${foundStuT.name}"`,
                message: `Taula "${solT.name}" present (+${tableRubric.points} pts).`,
                table: solT.name,
                maxPoints: tableRubric.points,
                pointsEarned: tableRubric.points
            });
        }

        const solCols = solT.columns || [];
        const stuCols = foundStuT.columns || [];

        solCols.forEach(solC => {
            expectedCols++;
            const colRubricId = `rel_col_${solT.name}.${solC.name}`;
            const colRubric = checkRubric([colRubricId, `${solT.name}.${solC.name}`, solC.name], 30);
            if (colRubric.enabled) totalMaxConfigured += colRubric.points;

            const foundC = stuCols.find(c => norm(c.name) === norm(solC.name));
            if (!foundC) {
                if (colRubric.enabled) {
                    feedback.push({
                        type: 'error',
                        diffType: 'missing',
                        itemLabel: `${solT.name}.${solC.name}`,
                        expected: `Columna "${solC.name}" ${solC.type ? `(${solC.type})` : ''} ${solC.isPK ? '[PK]' : ''}`,
                        actual: '(No entregada)',
                        message: `A la taula "${solT.name}" falta la columna "${solC.name}".`,
                        table: solT.name,
                        maxPoints: colRubric.points,
                        pointsEarned: 0
                    });
                }
                return;
            }

            matchedCols++;
            let colOk = true;

            // Validar Clau Primària
            if (Boolean(solC.isPK) !== Boolean(foundC.isPK)) {
                if (colRubric.enabled) {
                    feedback.push({
                        type: 'error',
                        diffType: 'mismatch',
                        itemLabel: `${solT.name}.${solC.name} (PK)`,
                        expected: solC.isPK ? 'Clau Primària (PK)' : 'No PK',
                        actual: foundC.isPK ? 'Clau Primària (PK)' : 'No PK',
                        message: `A la taula "${solT.name}", la columna "${solC.name}" ${solC.isPK ? 'ha de ser' : 'no ha de ser'} Clau Primària (PK).`,
                        table: solT.name
                    });
                }
                colOk = false;
            }

            // Validar Tipus de Dades
            if (solC.type) {
                const solTNorm = normalizeType(solC.type);
                const stuTNorm = normalizeType(foundC.type);
                if (solTNorm !== stuTNorm) {
                    if (colRubric.enabled) {
                        feedback.push({
                            type: 'warning',
                            diffType: 'mismatch',
                            itemLabel: `${solT.name}.${solC.name} (Tipus)`,
                            expected: solC.type,
                            actual: foundC.type || '(indefinit)',
                            message: `A la taula "${solT.name}", la columna "${solC.name}" és de tipus "${foundC.type || 'indefinit'}", s'esperava "${solC.type}".`,
                            table: solT.name
                        });
                    }
                    colOk = false;
                } else {
                    matchedTypes++;
                }
            }

            if (colOk) {
                if (colRubric.enabled) {
                    earnedScore += colRubric.points;
                    feedback.push({
                        type: 'success',
                        diffType: 'correct',
                        itemLabel: `${solT.name}.${solC.name}`,
                        expected: `Columna "${solC.name}" ${solC.type || ''}`,
                        actual: `Columna "${foundC.name}" ${foundC.type || ''}`,
                        message: `Columna "${solC.name}" a "${solT.name}" correcta (+${colRubric.points} pts).`,
                        table: solT.name,
                        maxPoints: colRubric.points,
                        pointsEarned: colRubric.points
                    });
                }
            } else if (colRubric.enabled) {
                feedback.push({
                    type: 'error',
                    diffType: 'mismatch',
                    itemLabel: `${solT.name}.${solC.name}`,
                    expected: 'Totes les propietats correctes',
                    actual: 'Propietats diferents a la solució',
                    message: `Discrepàncies a la columna "${solC.name}" de "${solT.name}".`,
                    table: solT.name,
                    maxPoints: colRubric.points,
                    pointsEarned: 0
                });
            }
        });

        // Columnes sobrants
        stuCols.forEach(stuC => {
            if (!solCols.some(c => norm(c.name) === norm(stuC.name))) {
                feedback.push({
                    type: 'warning',
                    diffType: 'surplus',
                    itemLabel: `${solT.name}.${stuC.name}`,
                    expected: '(No requerida a la solució)',
                    actual: `Columna "${stuC.name}"`,
                    message: `A la taula "${solT.name}", la columna "${stuC.name}" no és requerida.`,
                    table: solT.name
                });
            }
        });
    });

    // Taules sobrants de l'estudiant
    stuTables.forEach(stuT => {
        if (!solTables.some(t => norm(t.name) === norm(stuT.name))) {
            feedback.push({
                type: 'warning',
                diffType: 'surplus',
                itemLabel: stuT.name,
                expected: '(No requerida a la solució)',
                actual: `Taula "${stuT.name}"`,
                message: `La taula "${stuT.name}" no és requerida a la solució.`,
                table: stuT.name
            });
        }
    });

    // 2. Validar Claus Foranes (Fletxes de relació entre taules)
    solFKs.forEach((solF, idx) => {
        const fkRubricId = `rel_fk_${solF.id || `${solF.fromTable}.${solF.fromColumn}_${solF.toTable}`}`;
        const fkRubric = checkRubric([fkRubricId, `rel_fk_${solF.fromTable}.${solF.fromColumn}_${solF.toTable}`, solF.id], 40);
        if (fkRubric.enabled) totalMaxConfigured += fkRubric.points;

        const foundFK = stuFKs.find(f =>
            norm(f.fromTable) === norm(solF.fromTable) &&
            norm(f.fromColumn) === norm(solF.fromColumn) &&
            norm(f.toTable) === norm(solF.toTable) &&
            (!solF.toColumn || norm(f.toColumn) === norm(solF.toColumn))
        );

        if (!foundFK) {
            if (fkRubric.enabled) {
                feedback.push({
                    type: 'error',
                    diffType: 'missing',
                    itemLabel: `${solF.fromTable}.${solF.fromColumn} ➔ ${solF.toTable}`,
                    expected: `FK "${solF.fromTable}.${solF.fromColumn}" ➔ "${solF.toTable}${solF.toColumn ? '.' + solF.toColumn : ''}"`,
                    actual: '(No creada)',
                    message: `Falta la relació FK des de "${solF.fromTable}.${solF.fromColumn}" cap a "${solF.toTable}${solF.toColumn ? '.' + solF.toColumn : ''}".`,
                    maxPoints: fkRubric.points,
                    pointsEarned: 0
                });
            }
        } else {
            matchedFKs++;
            if (fkRubric.enabled) {
                earnedScore += fkRubric.points;
                feedback.push({
                    type: 'success',
                    diffType: 'correct',
                    itemLabel: `${solF.fromTable}.${solF.fromColumn} ➔ ${solF.toTable}`,
                    expected: `FK ➔ ${solF.toTable}`,
                    actual: `FK ➔ ${foundFK.toTable}`,
                    message: `FK "${solF.fromTable}.${solF.fromColumn}" ➔ "${solF.toTable}" correcta (+${fkRubric.points} pts).`,
                    maxPoints: fkRubric.points,
                    pointsEarned: fkRubric.points
                });
            }
        }
    });

    // FKs sobrants
    stuFKs.forEach(stuF => {
        const isExpected = solFKs.some(f =>
            norm(f.fromTable) === norm(stuF.fromTable) &&
            norm(f.fromColumn) === norm(stuF.fromColumn) &&
            norm(f.toTable) === norm(stuF.toTable)
        );
        if (!isExpected) {
            feedback.push({
                type: 'warning',
                diffType: 'surplus',
                itemLabel: `${stuF.fromTable}.${stuF.fromColumn} ➔ ${stuF.toTable}`,
                expected: '(No requerida a la solució)',
                actual: `FK "${stuF.fromTable}.${stuF.fromColumn}" ➔ "${stuF.toTable}"`,
                message: `La relació FK des de "${stuF.fromTable}.${stuF.fromColumn}" cap a "${stuF.toTable}" no és requerida.`
            });
        }
    });

    // Si la solució del professor està buida no es pot avaluar (0, no 100).
    const solucioBuida = solTables.length === 0 && solFKs.length === 0;

    // Nota estructural (pesos per defecte), independent de la rúbrica.
    const totalWeight = (solTables.length * 25) + (expectedCols * 35) + (solFKs.length * 40);
    const actualWeight = (matchedTables * 25) + (matchedCols * 20) + (matchedTypes * 15) + (matchedFKs * 40);
    const notaEstructural = totalWeight > 0 ? Math.round((actualWeight / totalWeight) * 100) : 0;

    let notaBase = 0;
    if (solucioBuida) {
        notaBase = 0;
    } else if (hasCustomRubric && totalMaxConfigured > 0) {
        // Només fem servir la rúbrica si realment aporta punts avaluables; si no, caiem a l'estructural.
        notaBase = Math.round((earnedScore / totalMaxConfigured) * 100);
    } else {
        notaBase = notaEstructural;
    }

    const penalitzacioPerIntent = Number(customConfig?.penalitzacioPerIntent) || 0;
    const penalitzacioTotal = penalitzacioPerIntent * Math.max(0, intentsFallitsPrevis);
    const scoreFinal = Math.max(0, Math.min(100, Math.round(notaBase - penalitzacioTotal)));

    if (penalitzacioTotal > 0) {
        feedback.push({
            type: 'warning',
            message: `Penalització aplicada: -${penalitzacioTotal} punts (-${penalitzacioPerIntent} pts per ${intentsFallitsPrevis} intents fallits previs).`
        });
    }

    if (feedback.length === 0) {
        feedback.push({
            type: 'success',
            message: 'Model Relacional de Taules completament correcte!'
        });
    }

    return {
        score: scoreFinal,
        notaBase,
        penalitzacioTotal,
        intentsFallitsPrevis,
        isCorrect: scoreFinal === 100 && feedback.every(f => f.type !== 'error'),
        feedback,
        summary: {
            tablesExpected: solTables.length,
            tablesMatched: matchedTables,
            columnsExpected: expectedCols,
            columnsMatched: matchedCols,
            typesMatched: matchedTypes,
            fksExpected: solFKs.length,
            fksMatched: matchedFKs
        }
    };
};
